import { computed, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { storeToRefs } from 'pinia'
import { useVisibilityStore } from '@/visibility/visibility.store'
import type { ObjectiveLevel } from '../objectives/objective_levels'
import { useGeneratedAssessmentsStore } from './generated_assessments.store'
import type { GeneratedAssessment } from './generated_assessment_types'

/**
 * A saved page's generated assessments, behind the Generate Assessment visibility flag: with the
 * flag off every list is empty, so no strip or heading opens for them.
 */
export function useSavedPageGeneratedAssessments(savedPageUuid: MaybeRefOrGetter<string>) {
  const generatedAssessmentsStore = useGeneratedAssessmentsStore()
  const { generateAssessmentVisibility } = storeToRefs(useVisibilityStore())

  const annualAssessments = computed<GeneratedAssessment[]>(() => {
    if (!generateAssessmentVisibility.value) return []
    return generatedAssessmentsStore.annualTargetFor(toValue(savedPageUuid))?.assessments ?? []
  })

  function assessmentsForLevel(level: ObjectiveLevel): GeneratedAssessment[] {
    if (!generateAssessmentVisibility.value) return []
    return generatedAssessmentsStore.objectiveTargetFor(toValue(savedPageUuid), level)?.assessments ?? []
  }

  const levelsWithGeneratedAssessments = computed<ObjectiveLevel[]>(() => {
    if (!generateAssessmentVisibility.value) return []
    return generatedAssessmentsStore
      .targetsFor(toValue(savedPageUuid))
      .filter((target) => target.target_type === 'objective' && target.assessments.length > 0)
      .map((target) => target.target_level as ObjectiveLevel)
  })

  return { annualAssessments, assessmentsForLevel, levelsWithGeneratedAssessments }
}

/**
 * Loads a saved page's generated assessments, requesting nothing while the flag is off. The page loads them once
 * it has shown; its strips read them. A flag turned on later loads them only for a shown page not yet loaded.
 */
export function useSavedPageGeneratedAssessmentsLoader(savedPageUuid: MaybeRefOrGetter<string>, isPageShown: MaybeRefOrGetter<boolean>) {
  const generatedAssessmentsStore = useGeneratedAssessmentsStore()
  const { generateAssessmentVisibility } = storeToRefs(useVisibilityStore())
  const pendingFetches = new Map<string, Promise<void>>()

  // Non-fatal: the strip is supplementary, so a failed fetch degrades to no strip rather than failing the page.
  async function requestGeneratedAssessments(uuid: string) {
    try {
      await generatedAssessmentsStore.fetchGeneratedAssessments(uuid)
    } catch (error) {
      console.error('Failed to fetch generated assessments:', error)
    } finally {
      pendingFetches.delete(uuid)
    }
  }

  function fetchGeneratedAssessments(): Promise<void> {
    if (!generateAssessmentVisibility.value) return Promise.resolve()
    const uuid = toValue(savedPageUuid)
    if (!pendingFetches.has(uuid)) pendingFetches.set(uuid, requestGeneratedAssessments(uuid))
    return pendingFetches.get(uuid) as Promise<void>
  }

  watch(generateAssessmentVisibility, (isVisible) => {
    if (!isVisible || !toValue(isPageShown) || generatedAssessmentsStore.isLoadedFor(toValue(savedPageUuid))) return
    void fetchGeneratedAssessments()
  })

  return { fetchGeneratedAssessments }
}
